import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AttachMoney,
  CheckCircleOutline,
  ExpandMore as ExpandMoreIcon,
  HighlightOff,
  History,
  LockOpenOutlined,
  LockOutlined,
  SwapHoriz,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ccyFormat } from '../../helpers/helper';
import DashboardService from '../../services/dashboard';

const ExpandMoreStyled = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    { props: ({ expand }) => !expand, style: { transform: 'rotate(0deg)' } },
    { props: ({ expand }) => !!expand, style: { transform: 'rotate(180deg)' } },
  ],
}));

const MiniStat = ({ icon, label, value, color }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        bgcolor: `${color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { sx: { color, fontSize: 16 } })}
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.1}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        ${ccyFormat(value)}
      </Typography>
    </Box>
  </Box>
);

const CashRegisterCard = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isOpen = entry.status === 'OPENED';
  const diffColor = entry.difference === 0
    ? theme.palette.success.main
    : theme.palette.error.main;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardHeader
        sx={{ pb: 0 }}
        title={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight={700}>
              {entry.open_at ? new Date(entry.open_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </Typography>
            <Chip
              size="small"
              icon={isOpen ? <LockOpenOutlined sx={{ fontSize: 14 }} /> : <LockOutlined sx={{ fontSize: 14 }} />}
              label={isOpen ? 'Abierta' : 'Cerrada'}
              color={isOpen ? 'success' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 600, height: 22 }}
            />
            {entry.difference !== 0 && (
              <Chip
                size="small"
                icon={entry.difference > 0
                  ? <CheckCircleOutline sx={{ fontSize: 14 }} />
                  : <HighlightOff sx={{ fontSize: 14 }} />}
                label={entry.difference > 0
                  ? `Sobrante $${ccyFormat(entry.difference)}`
                  : `Faltante $${ccyFormat(Math.abs(entry.difference))}`}
                color={entry.difference > 0 ? 'success' : 'error'}
                variant="filled"
                sx={{ fontWeight: 600, height: 22 }}
              />
            )}
          </Box>
        }
        subheader={
          <Box display="flex" gap={2} mt={0.5} flexWrap="wrap">
            {entry.open_at && (
              <Typography variant="caption" color="text.secondary">
                Apertura: {new Date(entry.open_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
            {entry.close_at && (
              <Typography variant="caption" color="text.secondary">
                Cierre: {new Date(entry.close_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            )}
          </Box>
        }
        action={
          <Tooltip title={expanded ? 'Menos detalle' : 'Ver detalle'}>
            <ExpandMoreStyled expand={expanded} onClick={() => setExpanded(e => !e)} size="small">
              <ExpandMoreIcon />
            </ExpandMoreStyled>
          </Tooltip>
        }
      />

      {/* Summary row always visible */}
      <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<AccountBalanceWallet />} label="Inicial" value={entry.initial_amount} color="#1976d2" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<AttachMoney />} label="Contado real" value={entry.final_cash_amount} color="#f9a825" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<AttachMoney />} label="Esperado" value={entry.expected_cash_amount} color="#7b1fa2" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<TrendingUp />} label="Señas" value={entry.total_deposits} color="#1565c0" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<TrendingDown />} label="Devoluciones" value={entry.total_refunds} color="#ef6c00" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <MiniStat icon={<SwapHoriz />} label="Diferencia" value={entry.difference} color={diffColor} />
          </Grid>
        </Grid>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            {entry.cash_register_id && (
              <Box display="flex" gap={1}>
                <Typography variant="caption" color="text.secondary" minWidth={100}>ID caja:</Typography>
                <Typography variant="caption" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{entry.cash_register_id}</Typography>
              </Box>
            )}
            {entry.open_at && (
              <Box display="flex" gap={1}>
                <Typography variant="caption" color="text.secondary" minWidth={100}>Apertura:</Typography>
                <Typography variant="caption" fontWeight={600}>{new Date(entry.open_at).toLocaleString('es-AR')}</Typography>
              </Box>
            )}
            {entry.close_at && (
              <Box display="flex" gap={1}>
                <Typography variant="caption" color="text.secondary" minWidth={100}>Cierre:</Typography>
                <Typography variant="caption" fontWeight={600}>{new Date(entry.close_at).toLocaleString('es-AR')}</Typography>
              </Box>
            )}
            {entry.difference !== 0 && (
              <Box
                p={1.5}
                borderRadius={2}
                sx={{ bgcolor: entry.difference > 0 ? 'success.light' : 'error.light', mt: 1 }}
                display="flex"
                alignItems="center"
                gap={1}
              >
                {entry.difference > 0
                  ? <CheckCircleOutline sx={{ color: 'success.dark', fontSize: 18 }} />
                  : <HighlightOff sx={{ color: 'error.dark', fontSize: 18 }} />}
                <Typography variant="caption" fontWeight={600} color={entry.difference > 0 ? 'success.dark' : 'error.dark'}>
                  {entry.difference > 0
                    ? `Sobrante de $${ccyFormat(entry.difference)}`
                    : `Faltante de $${ccyFormat(Math.abs(entry.difference))}`}
                </Typography>
              </Box>
            )}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const CashRegisterHistory = ({ storeId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toLocalDate = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateFrom, setDateFrom] = useState(toLocalDate(firstOfMonth));
  const [dateTo, setDateTo] = useState(toLocalDate(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const load = async (from, to) => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await DashboardService.getCashRegisterHistory(storeId, { date_from: from, date_to: to });
      setData(Array.isArray(resp) ? resp : []);
    } catch {
      setError('No se pudo cargar el historial de cajas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(dateFrom, dateTo); }, [storeId]);

  const handleDateChange = (field, value) => {
    const next = { dateFrom, dateTo, [field]: value };
    if (field === 'dateFrom') setDateFrom(value);
    else setDateTo(value);
    // Debounce: espera 400ms tras el último cambio antes de llamar a la API
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(next.dateFrom, next.dateTo);
    }, 400);
  };

  // Totales del período
  const totals = data.reduce((acc, e) => ({
    initial: acc.initial + (e.initial_amount || 0),
    final: acc.final + (e.final_cash_amount || 0),
    expected: acc.expected + (e.expected_cash_amount || 0),
    deposits: acc.deposits + (e.total_deposits || 0),
    refunds: acc.refunds + (e.total_refunds || 0),
    difference: acc.difference + (e.difference || 0),
  }), { initial: 0, final: 0, expected: 0, deposits: 0, refunds: 0, difference: 0 });

  return (
    <Box p={isMobile ? 1.5 : 3}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3} flexWrap="wrap" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <History sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Historial de Cajas</Typography>
            <Typography variant="body2" color="text.secondary">Consultá y analizá cajas pasadas</Typography>
          </Box>
        </Box>

        {/* Filtro fechas */}
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={dateFrom}
            onChange={e => handleDateChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={dateTo}
            onChange={e => handleDateChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
        </Box>
      </Box>

      {/* Totales del período */}
      {!loading && data.length > 0 && (
        <>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 3, mb: 3, bgcolor: 'primary.50' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="caption" color="primary.main" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} display="block" mb={1.5}>
                Resumen del período · {data.length} caja{data.length !== 1 ? 's' : ''}
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { icon: <AccountBalanceWallet />, label: 'Total inicial', value: totals.initial, color: '#1976d2' },
                  { icon: <AttachMoney />,          label: 'Total contado', value: totals.final,   color: '#f9a825' },
                  { icon: <AttachMoney />,          label: 'Total esperado', value: totals.expected, color: '#7b1fa2' },
                  { icon: <TrendingUp />,           label: 'Total señas',   value: totals.deposits, color: '#1565c0' },
                  { icon: <TrendingDown />,         label: 'Total devol.',  value: totals.refunds,  color: '#ef6c00' },
                  { icon: <SwapHoriz />,            label: 'Diferencia total', value: totals.difference,
                    color: totals.difference === 0 ? theme.palette.success.main : theme.palette.error.main },
                ].map((s, i) => (
                  <Grid item xs={6} sm={4} md={2} key={i}>
                    <MiniStat {...s} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Content */}
      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box display="flex" justifyContent="center" py={6}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {!loading && !error && data.length === 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={1}>
          <History sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h6" color="text.secondary">Sin registros</Typography>
          <Typography variant="body2" color="text.disabled">No hay cajas en el período seleccionado.</Typography>
        </Box>
      )}

      {!loading && !error && data.length > 0 && (
        <Stack spacing={2}>
          {data.map((entry, i) => (
            <CashRegisterCard key={entry.cash_register_id || i} entry={entry} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default CashRegisterHistory;
